import React, { useState, useEffect } from 'react';
import './BusinessContextEditor.css';

const BusinessContextEditor = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [context, setContext] = useState({
    industry: '',
    industry_type: 'general',
    industry_settings: {},
    service_area: '',
    years_in_business: null,
    team_size: null,
    certifications: [],
    typical_response_time: '',
    emergency_available: false,
    tone_of_voice: 'professional',
    key_selling_points: [],
    services_offered: { list: [], details: [] },
    frequently_asked_questions: []
  });

  const [newCertification, setNewCertification] = useState('');
  const [newSellingPoint, setNewSellingPoint] = useState('');
  const [newService, setNewService] = useState({ name: '', description: '', priceRange: '' });
  const [newFAQ, setNewFAQ] = useState({ question: '', answer: '', category: 'general' });

  const [phoneNumber, setPhoneNumber] = useState(null);
  const [forwardingForm, setForwardingForm] = useState({
    forwarding_enabled: false,
    forwarding_number: '',
    forwarding_mode: 'ai_fallback'
  });
  const [savingForwarding, setSavingForwarding] = useState(false);
  const [forwardingError, setForwardingError] = useState('');
  const [forwardingSuccess, setForwardingSuccess] = useState('');

  useEffect(() => {
    loadBusinessContext();
    loadPhoneNumber();
  }, []);

  const loadPhoneNumber = async () => {
    try {
      const token = localStorage.getItem('oakdesk_token');
      const response = await fetch('/business/numbers', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        const voiceNumber = (data.numbers || []).find((n) => n.channel === 'voice') || null;
        setPhoneNumber(voiceNumber);
        if (voiceNumber) {
          setForwardingForm({
            forwarding_enabled: voiceNumber.forwarding_enabled || false,
            forwarding_number: voiceNumber.forwarding_number || '',
            forwarding_mode: voiceNumber.forwarding_mode || 'ai_fallback'
          });
        }
      }
    } catch (err) {
      console.error('Failed to load phone number', err);
    }
  };

  const saveForwarding = async () => {
    if (!phoneNumber) return;
    setForwardingError('');
    setForwardingSuccess('');
    if (forwardingForm.forwarding_enabled && !forwardingForm.forwarding_number.trim()) {
      setForwardingError('Enter a forwarding number first');
      return;
    }
    setSavingForwarding(true);
    try {
      const token = localStorage.getItem('oakdesk_token');
      const response = await fetch(`/business/numbers/${phoneNumber.id}/forwarding`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(forwardingForm),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || 'Failed to save call forwarding');
      }
      setForwardingSuccess('Call forwarding settings saved');
      await loadPhoneNumber();
    } catch (err) {
      setForwardingError(err.message || 'Failed to save call forwarding');
    } finally {
      setSavingForwarding(false);
    }
  };

  const loadBusinessContext = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('oakdesk_token');
      const response = await fetch('/business/context', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        // Normalize services_offered to the internal {list, details} format
        let servicesOffered;
        if (Array.isArray(data.services_offered)) {
          servicesOffered = {
            list: data.services_offered.map((s) => s.name),
            details: data.services_offered.map((s) => ({
              name: s.name,
              description: s.description || '',
              priceRange: s.price_range || '',
            })),
          };
        } else {
          servicesOffered = data.services_offered || { list: [], details: [] };
        }
        setContext({
          ...data,
          services_offered: servicesOffered,
          frequently_asked_questions: data.frequently_asked_questions || [],
          certifications: data.certifications || [],
          key_selling_points: data.key_selling_points || [],
          industry_type: data.industry_type || 'general',
          industry_settings: data.industry_settings || {},
        });
      }
    } catch (err) {
      setError('Failed to load business context');
    } finally {
      setLoading(false);
    }
  };

  const saveContext = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Normalize services_offered: always send as a list of ServiceInfo objects
      const servicesRaw = context.services_offered;
      const servicesList = Array.isArray(servicesRaw)
        ? servicesRaw
        : Array.isArray(servicesRaw?.details)
          ? servicesRaw.details.map((s) => ({
              name: s.name,
              description: s.description || s.name,
              price_range: s.priceRange || s.price_range || null,
            }))
          : [];

      const contextToSave = {
        ...context,
        services_offered: servicesList,
      };

      const token = localStorage.getItem('oakdesk_token');
      const response = await fetch('/business/context', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(contextToSave),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to save business context');
      }

      setSuccess('Business context saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setContext({
        ...context,
        certifications: [...(context.certifications || []), newCertification]
      });
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setContext({
      ...context,
      certifications: context.certifications.filter((_, i) => i !== index)
    });
  };

  const addSellingPoint = () => {
    if (newSellingPoint.trim()) {
      setContext({
        ...context,
        key_selling_points: [...(context.key_selling_points || []), newSellingPoint]
      });
      setNewSellingPoint('');
    }
  };

  const removeSellingPoint = (index) => {
    setContext({
      ...context,
      key_selling_points: context.key_selling_points.filter((_, i) => i !== index)
    });
  };

  const addService = () => {
    if (newService.name.trim()) {
      const details = [...(context.services_offered.details || []), newService];
      setContext({
        ...context,
        services_offered: {
          list: details.map(s => s.name),
          details: details
        }
      });
      setNewService({ name: '', description: '', priceRange: '' });
    }
  };

  const removeService = (index) => {
    const details = context.services_offered.details.filter((_, i) => i !== index);
    setContext({
      ...context,
      services_offered: {
        list: details.map(s => s.name),
        details: details
      }
    });
  };

  const addFAQ = () => {
    if (newFAQ.question.trim() && newFAQ.answer.trim()) {
      setContext({
        ...context,
        frequently_asked_questions: [...(context.frequently_asked_questions || []), newFAQ]
      });
      setNewFAQ({ question: '', answer: '', category: 'general' });
    }
  };

  const removeFAQ = (index) => {
    setContext({
      ...context,
      frequently_asked_questions: context.frequently_asked_questions.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return <div className="loading-spinner">Loading business context...</div>;
  }

  return (
    <div className="business-context-editor">
      <div className="editor-header">
        <div>
          <h1>Business Settings</h1>
          <p className="text-secondary">Manage your business information and AI configuration</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={saveContext} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="editor-sections">
        {/* Basic Info */}
        <section className="editor-section card">
          <h2>📋 Basic Information</h2>
          
          <label>
            Industry
            <select
              value={context.industry}
              onChange={(e) => setContext({...context, industry: e.target.value})}
            >
              <option value="">Select industry</option>
              <option value="Plumbing">Plumbing</option>
              <option value="HVAC">HVAC</option>
              <option value="Electrical">Electrical</option>
              <option value="Legal">Legal Services</option>
              <option value="Medical">Medical/Healthcare</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Automotive">Automotive</option>
              <option value="Restaurant">Restaurant/Food Service</option>
              <option value="Retail">Retail</option>
              <option value="Other">Other</option>
            </select>
          </label>

          <label>
            Service Area
            <input
              type="text"
              value={context.service_area || ''}
              onChange={(e) => setContext({...context, service_area: e.target.value})}
              placeholder="Phoenix, AZ and surrounding areas"
            />
          </label>

          <div className="form-row">
            <label>
              Years in Business
              <input
                type="number"
                value={context.years_in_business || ''}
                onChange={(e) => setContext({...context, years_in_business: parseInt(e.target.value) || null})}
                placeholder="5"
              />
            </label>

            <label>
              Team Size
              <input
                type="number"
                value={context.team_size || ''}
                onChange={(e) => setContext({...context, team_size: parseInt(e.target.value) || null})}
                placeholder="10"
              />
            </label>
          </div>
        </section>

        {/* Services */}
        <section className="editor-section card">
          <h2>🛠️ Services Offered</h2>
          
          <div className="items-list">
            {context.services_offered.details?.map((service, index) => (
              <div key={index} className="item-card">
                <div className="item-content">
                  <strong>{service.name}</strong>
                  {service.description && <p>{service.description}</p>}
                  {service.priceRange && <span className="badge">{service.priceRange}</span>}
                </div>
                <button className="btn-remove" onClick={() => removeService(index)}>×</button>
              </div>
            ))}
          </div>

          <div className="add-item-form">
            <input
              type="text"
              placeholder="Service name"
              value={newService.name}
              onChange={(e) => setNewService({...newService, name: e.target.value})}
            />
            <input
              type="text"
              placeholder="Description"
              value={newService.description}
              onChange={(e) => setNewService({...newService, description: e.target.value})}
            />
            <input
              type="text"
              placeholder="Price range (optional)"
              value={newService.priceRange}
              onChange={(e) => setNewService({...newService, priceRange: e.target.value})}
            />
            <button className="btn btn-ghost" onClick={addService}>+ Add Service</button>
          </div>
        </section>

        {/* FAQs */}
        <section className="editor-section card">
          <h2>❓ Frequently Asked Questions</h2>
          
          <div className="items-list">
            {context.frequently_asked_questions?.map((faq, index) => (
              <div key={index} className="item-card">
                <div className="item-content">
                  <strong>Q: {faq.question}</strong>
                  <p>A: {faq.answer}</p>
                  {faq.category && <span className="badge">{faq.category}</span>}
                </div>
                <button className="btn-remove" onClick={() => removeFAQ(index)}>×</button>
              </div>
            ))}
          </div>

          <div className="add-item-form">
            <input
              type="text"
              placeholder="Question"
              value={newFAQ.question}
              onChange={(e) => setNewFAQ({...newFAQ, question: e.target.value})}
            />
            <textarea
              placeholder="Answer"
              value={newFAQ.answer}
              onChange={(e) => setNewFAQ({...newFAQ, answer: e.target.value})}
              rows="2"
            />
            <select
              value={newFAQ.category}
              onChange={(e) => setNewFAQ({...newFAQ, category: e.target.value})}
            >
              <option value="general">General</option>
              <option value="pricing">Pricing</option>
              <option value="scheduling">Scheduling</option>
              <option value="services">Services</option>
            </select>
            <button className="btn btn-ghost" onClick={addFAQ}>+ Add FAQ</button>
          </div>
        </section>

        {/* AI Configuration */}
        <section className="editor-section card">
          <h2>🤖 AI Configuration</h2>
          
          <label>
            Tone of Voice
            <select
              value={context.tone_of_voice}
              onChange={(e) => setContext({...context, tone_of_voice: e.target.value})}
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="casual">Casual</option>
              <option value="formal">Formal</option>
            </select>
          </label>

          <label>
            Typical Response Time
            <select
              value={context.typical_response_time}
              onChange={(e) => setContext({...context, typical_response_time: e.target.value})}
            >
              <option value="">Select response time</option>
              <option value="within-1-hour">Within 1 hour</option>
              <option value="within-2-hours">Within 2 hours</option>
              <option value="within-24-hours">Within 24 hours</option>
              <option value="next-business-day">Next business day</option>
            </select>
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={context.emergency_available}
              onChange={(e) => setContext({...context, emergency_available: e.target.checked})}
            />
            <span>24/7 Emergency Services Available</span>
          </label>
        </section>

        {/* Certifications */}
        <section className="editor-section card">
          <h2>🏆 Certifications & Credentials</h2>
          
          <div className="tags-list">
            {context.certifications?.map((cert, index) => (
              <div key={index} className="tag">
                {cert}
                <button onClick={() => removeCertification(index)}>×</button>
              </div>
            ))}
          </div>

          <div className="add-tag-form">
            <input
              type="text"
              placeholder="Add certification"
              value={newCertification}
              onChange={(e) => setNewCertification(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCertification()}
            />
            <button className="btn btn-ghost" onClick={addCertification}>+ Add</button>
          </div>
        </section>

        {/* Key Selling Points */}
        <section className="editor-section card">
          <h2>⭐ Key Selling Points</h2>
          
          <div className="tags-list">
            {context.key_selling_points?.map((point, index) => (
              <div key={index} className="tag">
                {point}
                <button onClick={() => removeSellingPoint(index)}>×</button>
              </div>
            ))}
          </div>

          <div className="add-tag-form">
            <input
              type="text"
              placeholder="Add selling point"
              value={newSellingPoint}
              onChange={(e) => setNewSellingPoint(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addSellingPoint()}
            />
            <button className="btn btn-ghost" onClick={addSellingPoint}>+ Add</button>
          </div>
        </section>

        {/* Phone Number & Call Forwarding */}
        <section className="editor-section card">
          <h2>📞 Phone Number & Call Forwarding</h2>

          {!phoneNumber ? (
            <p className="text-secondary">No phone number assigned yet — contact support to get set up.</p>
          ) : (
            <>
              <label>
                Your Number
                <input type="text" value={phoneNumber.phone_number} disabled />
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={forwardingForm.forwarding_enabled}
                  onChange={(e) => setForwardingForm({ ...forwardingForm, forwarding_enabled: e.target.checked })}
                />
                <span>Forward calls to another number</span>
              </label>

              {forwardingForm.forwarding_enabled && (
                <>
                  <label>
                    Forwarding Number
                    <input
                      type="text"
                      value={forwardingForm.forwarding_number}
                      onChange={(e) => setForwardingForm({ ...forwardingForm, forwarding_number: e.target.value })}
                      placeholder="+27821234567"
                    />
                  </label>

                  <label>
                    Forwarding Mode
                    <select
                      value={forwardingForm.forwarding_mode}
                      onChange={(e) => setForwardingForm({ ...forwardingForm, forwarding_mode: e.target.value })}
                    >
                      <option value="ai_fallback">Let the AI try first, forward only if it can't help</option>
                      <option value="always">Always forward calls to this number</option>
                    </select>
                  </label>
                </>
              )}

              {forwardingError && <div className="alert alert-error">{forwardingError}</div>}
              {forwardingSuccess && <div className="alert alert-success">{forwardingSuccess}</div>}

              <button className="btn btn-primary" onClick={saveForwarding} disabled={savingForwarding}>
                {savingForwarding ? 'Saving...' : '💾 Save Call Forwarding'}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default BusinessContextEditor;
